/**
 * Authentication Service - Updated with Store Management
 * Handles login, logout, registration, user session management, and store selection
 */

import { apiService, apiClient, tokenManager } from "./api.js";
import { setAppInsightsUser, clearAppInsightsUser } from "../appInsights.js";
import useSpecialPricingStore from "../store/specialPricingStore.js";

class AuthService {
  constructor() {
    this.currentUser = null;
    this.currentCompany = null;
    this.currentStore = null;
    this.availableStores = [];
  }

  /**
   * Login user with email/username and password
   * @param {object} formData - Login form data { email?, username?, password }
   * @returns {Promise<object>} User data and authentication info
   */
  async login(formData) {
    try {
      // Clear any existing authentication data
      this.logout();

      // Prepare login data based on new DTO structure
      const loginData = {
        email: formData.email || "",
        username: formData.username || "",
        password: formData.password,
      };

      // Call login endpoint
      const response = await apiService.post(
        "/storefront/login",
        loginData,
      );

      // Debug logging
      console.log('Login response:', response);
      console.log('MustChangePassword check:', response.Status === "MustChangePassword", response.status === "MustChangePassword", response.MustChangePassword === true);

      // Handle MustChangePassword response FIRST - check this before other scenarios
      // Check both Status (uppercase) and status (lowercase) for compatibility
      if (response.Status === "MustChangePassword" || response.status === "MustChangePassword" || response.MustChangePassword === true) {
        console.log('MustChangePassword detected! Token:', response.PasswordResetToken || response.passwordResetToken);
        return {
          success: false,
          mustChangePassword: true,
          passwordResetToken: response.PasswordResetToken || response.passwordResetToken,
          email: response.Email || response.email,
          userId: response.UserId || response.userId,
          message: response.Message || response.message || "Your password has expired and must be changed before continuing."
        };
      }

      // Handle different response scenarios
      if (response.Status === "ProfileConfirmationRequired") {
        // User needs to confirm their profile
        return {
          success: false,
          requiresProfileConfirmation: true,
          userId: response.UserId,
          username: response.Username,
          email: response.Email,
          mustChangePassword: response.MustChangePassword,
          message: response.Message,
        };
      }

      if (response.status === "Success" && response.token) {
        // Successful login
        const userData = {
          id: response.user.id,
          email: response.user.email,
          firstName: response.user.firstName,
          lastName: response.user.lastName,
          userTypeId: response.user.userTypeId,
          optStatusId: response.user.optStatusId,
          companyId: response.user.companyId,
          companies: response.user.companies || [],
          roleIds: response.user.roleIds || [],
          roles: response.user.roles || [],
          claims: response.user.claims || [],
          mustChangePassword: response.user.MustChangePassword || false,
        };

        // Store authentication data. KAN-149: the JWT is delivered as an HttpOnly cookie and
        // is intentionally NOT persisted to localStorage. We keep the non-secret `user` object
        // (UI gating) and `session_expires_at` (session-expiry warning) only.
        tokenManager.setUser(userData);
        setAppInsightsUser(userData);
        if (response.expiration) {
          tokenManager.setSessionExpiry(response.expiration);
        }

        // Set primary company
        if (userData.companies && userData.companies.length > 0) {
          const primaryCompany = userData.companies[0];
          tokenManager.setCurrentCompany({
            id: primaryCompany.companyId,
            name: primaryCompany.companyName,
            slug: primaryCompany.slug,
            displayName: primaryCompany.displayName,
            isMed: primaryCompany.isMed || false,
            isRec: primaryCompany.isRec || false,
            allowCustomCaseUnitLabels: primaryCompany.allowCustomCaseUnitLabels || false,
            moduleBackOrders: primaryCompany.moduleBackOrders === true,
          });
          this.currentCompany = tokenManager.getCurrentCompany();
        }

        this.currentUser = userData;

        let nextStep = "home";

        if (userData.userTypeId === 1 || userData.userTypeId === 3) {
          // Employee or Brand user — skip store fetching, use companies for preview mode
          this.availableStores = [];
          this.currentStore = null;

          if (!userData.companies || userData.companies.length === 0) {
            nextStep = "no_stores";
          } else if (userData.companies.length === 1) {
            // Single company — already auto-selected at lines above
            nextStep = "home";
          } else {
            // Multiple companies — need selection
            nextStep = "select_company";
          }

          return {
            success: true,
            user: userData,
            token: response.token,
            expiresAt: response.expiration,
            message: "Login successful",
            stores: [],
            currentStore: null,
            nextStep,
            isEmployeePreview: true,
          };
        }

        // Customer flow — fetch available stores
        await this.fetchUserStores();

        if (this.availableStores.length === 0) {
          nextStep = "no_stores";
        } else if (this.availableStores.length === 1) {
          // Auto-select the single store
          await this.selectStore(this.availableStores[0]);
          nextStep = "home";
        } else {
          // Multiple stores - need selection
          nextStep = "select_store";
        }

        return {
          success: true,
          user: userData,
          token: response.token,
          expiresAt: response.expiration,
          message: "Login successful",
          stores: this.availableStores,
          currentStore: this.currentStore,
          nextStep,
        };
      }

      // Handle error responses
      return {
        success: false,
        message: response.Message || "Login failed",
        status: response.Status,
      };
    } catch (error) {
      console.error("Login error:", error);

      // Handle specific error cases
      if (error.status === 401) {
        return {
          success: false,
          message: "Invalid email/username or password",
          isInvalidCredentials: true,
        };
      }

      if (error.status === 403) {
        return {
          success: false,
          message:
            error.message ||
            "Your account is not active. Please contact administrator.",
          isAccountInactive: true,
        };
      }

      if (error.status === 423) {
        // Account is locked due to too many failed attempts
        return {
          success: false,
          message: error.message || "Account is temporarily locked due to too many failed login attempts.",
          statusCode: 423,
          remainingMinutes: error.remainingMinutes || error.data?.remainingMinutes,
          lockoutEnd: error.lockoutEnd || error.data?.lockoutEnd,
          isAccountLocked: true,
        };
      }

      return {
        success: false,
        message: error.message || "Login failed. Please try again.",
        error: error,
      };
    }
  }

  /**
   * Fetch available stores for the current user WITH cart data
   * This is justified because store selection needs to show cart context
   * @returns {Promise<Array>} Array of available stores with cart totals
   */
  async fetchUserStores() {
    try {
      const user = this.getCurrentUser();
      if (!user?.id) {
        throw new Error("No authenticated user found");
      }

      // Shop-owned, self-scoped stores read (F2). The server returns the fully-shaped store
      // objects — route/company/payment/sales-rep detail plus open-cart totals — that the
      // storefront used to assemble client-side from an admin OData deep-expand.
      const response = await apiService.get("/storefront/stores");
      this.availableStores = response.data || [];

      // The server only returns active stores, so a selected store that is no longer in the
      // list has been inactivated or archived. Drop the selection — every caller's next read
      // of needsStoreSelection() then sends the user back to the picker.
      this._dropSelectedStoreIfUnavailable();

      return this.availableStores;
    } catch (error) {
      console.error("Error fetching user stores:", error);
      console.error("Error details:", error.message, error.stack);
      this.availableStores = [];
      return [];
    }
  }

  /**
   * Clear the selected store when it is no longer one of the user's available stores.
   * Only ever called after a successful stores fetch — a failed fetch empties the list
   * too, and must not cost the user their selection.
   * @private
   */
  _dropSelectedStoreIfUnavailable() {
    const selected = this.getCurrentStore();
    if (!selected) return;

    if (this.availableStores.some((s) => s.id === selected.id)) return;

    console.warn(
      `Selected store ${selected.id} is no longer available; clearing the selection.`
    );
    this.currentStore = null;
    tokenManager.removeCurrentStore();
    useSpecialPricingStore.getState().clear();
    this._notifyAuthStateChange();
  }

  /**
   * Select a store for the current session
   * @param {object} store - Store object to select
   * @returns {Promise<boolean>} Success status
   */
  async selectStore(store) {
    try {
      if (!store || !store.id) {
        throw new Error("Invalid store provided");
      }

      // Verify user has access to this store
      const hasAccess = this.availableStores.some((s) => s.id === store.id);
      if (!hasAccess) {
        throw new Error("User does not have access to this store");
      }

      // Store the selected store
      this.currentStore = store;
      tokenManager.setCurrentStore(store);

      // Drop the previous client's special-pricing overlay; it reloads for the
      // new client on the next product/cart fetch (ensureLoaded).
      useSpecialPricingStore.getState().clear();

      // Notify auth state change
      this._notifyAuthStateChange();

      return true;
    } catch (error) {
      console.error("Error selecting store:", error);
      return false;
    }
  }

  /**
   * Get current selected store
   * @returns {object|null} Current store data
   */
  getCurrentStore() {
    if (!this.currentStore) {
      this.currentStore = tokenManager.getCurrentStore();
    }
    return this.currentStore;
  }

  /**
   * Get all available stores for current user
   * @returns {Array} Array of available stores
   */
  getAvailableStores() {
    return this.availableStores || [];
  }

  /**
   * Switch to different store
   * @param {object} store - Store to switch to
   * @returns {boolean} Success status
   */
  async switchStore(store) {
    const success = await this.selectStore(store);
    if (success) {
      // Clear cart when switching stores since carts are store-specific
      try {
        // Import cart service dynamically to avoid circular dependencies
        const { default: cartService } = await import("./cartServices.js");
        cartService.reset();
      } catch (error) {
        console.warn("Could not reset cart when switching stores:", error);
      }
    }
    return success;
  }

  /**
   * Check if user has access to multiple stores
   * @returns {boolean} Whether user has multiple store access
   */
  hasMultipleStores() {
    return this.availableStores.length > 1;
  }

  /**
   * Check if user has access to any stores
   * @returns {boolean} Whether user has store access
   */
  hasStoreAccess() {
    if (this.isEmployee()) {
      const user = this.getCurrentUser();
      return (user?.companies?.length || 0) > 0;
    }
    return this.availableStores.length > 0;
  }

  /**
   * Get store display name with city
   * @param {object} store - Store object
   * @returns {string} Formatted store name
   */
  getStoreDisplayName(store = null) {
    const storeToUse = store || this.getCurrentStore();
    if (!storeToUse) return "No Store Selected";

    return `${storeToUse.name} - ${storeToUse.city}`;
  }

  /**
   * Validate current token
   * @returns {Promise<object>} Validation result
   */
  async validateToken() {
    try {
      // KAN-149: validity rides on the HttpOnly cookie (sent automatically). A 200 means the
      // cookie is valid; a 401 throws and is handled below. No localStorage token to check.
      const response = await apiService.get("/storefront/validate-token");
      return { success: true, data: response };
    } catch (error) {
      console.error("Token validation error:", error);

      // Token is invalid, clear authentication data
      this.logout();
      return {
        success: false,
        message: "Token validation failed",
        error: error,
      };
    }
  }

  /**
   * True when the auth cookie belongs to a different user than the one cached here. The shop and
   * the admin portal share one cookie, so signing in to the admin portal (or as anyone else) in
   * another tab swaps the identity under a cached shop session.
   * @param {object} validation the /storefront/validate-token response
   * @private
   */
  _cookieBelongsToSomeoneElse(validation) {
    const cookieUserId = validation?.userId;
    const cachedUserId = tokenManager.getUser()?.id;
    return cookieUserId != null && cachedUserId != null && String(cookieUserId) !== String(cachedUserId);
  }

  /**
   * Check, when the shop's tab comes back into view, whether the sign-in changed in another tab.
   * If it did, clears this user's cached session and returns true; the caller reloads, and
   * initialize() then picks up whoever the cookie belongs to. Errors (including an expired cookie,
   * which the API layer already handles) count as no change.
   * @returns {Promise<boolean>}
   */
  async checkSignedInUserChanged() {
    if (!tokenManager.getUser()) return false;
    try {
      const validation = await apiService.get("/storefront/validate-token");
      if (!this._cookieBelongsToSomeoneElse(validation)) return false;
      this.logout();
      return true;
    } catch {
      return false;
    }
  }

  /**
   * Logout user and clear all authentication data
   */
  logout() {
    clearAppInsightsUser();
    tokenManager.clearAll();
    useSpecialPricingStore.getState().clear();
    this.currentUser = null;
    this.currentCompany = null;
    this.currentStore = null;
    this.availableStores = [];

    // Clear cart when logging out
    try {
      import("./cartServices.js").then(({ default: cartService }) => {
        cartService.reset();
      });
    } catch (error) {
      console.warn("Could not reset cart on logout:", error);
    }
  }

  /**
   * User-initiated logout (KAN-145): invalidate the token server-side by rotating the
   * user's security stamp, then clear local state. Distinct from logout(), which is also
   * used for internal local cleanup (login / validateToken / initialize) where no server
   * call is wanted. The cookie is sent automatically (withCredentials); the local clear
   * runs immediately without waiting on the best-effort request.
   */
  signOut() {
    // KAN-149: the auth cookie rides automatically (withCredentials) — there's no localStorage
    // token to attach. Best-effort server-side invalidation (rotates the security stamp +
    // clears the cookie), then clear local state.
    apiService.post("/storefront/logout", null).catch(() => {
      // best-effort — local logout still happens below
    });
    this.logout();
  }

  /**
   * Get current authenticated user
   * @returns {object|null} Current user data
   */
  getCurrentUser() {
    if (!this.currentUser) {
      this.currentUser = tokenManager.getUser();
    }
    return this.currentUser;
  }

  /**
   * Get current company
   * @returns {object|null} Current company data
   */
  getCurrentCompany() {
    if (!this.currentCompany) {
      this.currentCompany = tokenManager.getCurrentCompany();
    }
    return this.currentCompany;
  }

  /**
   * Switch to different company (for multi-company users)
   * @param {object} company - Company to switch to
   * @returns {boolean} Success status
   */
  switchCompany(company) {
    try {
      const user = this.getCurrentUser();
      if (!user || !user.companies) {
        return false;
      }

      // Verify user has access to this company
      const hasAccess = user.companies.some((c) => c.companyId === company.id);
      if (!hasAccess) {
        console.error("User does not have access to company:", company.id);
        return false;
      }

      tokenManager.setCurrentCompany(company);
      this.currentCompany = company;
      return true;
    } catch (error) {
      console.error("Error switching company:", error);
      return false;
    }
  }

  /**
   * Check if user is authenticated
   * @returns {boolean} Whether user is authenticated
   */
  isAuthenticated() {
    // KAN-149: the JWT lives only in the HttpOnly cookie (unreadable by JS), so client-side
    // auth state is gated on the non-secret `user` object. The cookie is the real authority —
    // the backend enforces it on every request (and the validate-token / session checks).
    return !!tokenManager.getUser();
  }

  /**
   * Check if user is fully authenticated with store selected
   * @returns {boolean} Whether user is fully authenticated
   */
  isFullyAuthenticated() {
    if (this.isEmployee()) {
      return this.isAuthenticated() && !!this.getCurrentCompany();
    }
    return this.isAuthenticated() && !!this.getCurrentStore();
  }

  /**
   * Check if user has specific role
   * @param {string} roleName - Role name to check
   * @returns {boolean} Whether user has the role
   */
  hasRole(roleName) {
    const user = this.getCurrentUser();
    if (!user || !user.roles) return false;

    return user.roles.includes(roleName);
  }

  /**
   * Check if user has specific claim/permission
   * @param {string} claimName - Claim name to check
   * @returns {boolean} Whether user has the claim
   */
  hasClaim(claimName) {
    const user = this.getCurrentUser();
    if (!user || !user.claims) return false;

    return user.claims.includes(claimName);
  }

  /**
   * Check if user has access to specific company
   * @param {number} companyId - Company ID to check
   * @returns {boolean} Whether user has access to the company
   */
  hasCompanyAccess(companyId) {
    const user = this.getCurrentUser();
    if (!user || !user.companies) return false;

    return user.companies.some((c) => c.companyId === companyId);
  }

  /**
   * Get user's company list
   * @returns {array} Array of companies user has access to
   */
  getUserCompanies() {
    const user = this.getCurrentUser();
    return user?.companies || [];
  }

  /**
   * Check if user needs to change password
   * @returns {boolean} Whether user must change password
   */
  mustChangePassword() {
    const user = this.getCurrentUser();
    return user?.mustChangePassword === true;
  }

  /**
   * Get user's full name
   * @returns {string} User's full name
   */
  getUserDisplayName() {
    const user = this.getCurrentUser();
    if (!user) return "";

    if (user.firstName && user.lastName) {
      return `${user.firstName} ${user.lastName}`;
    }

    return user.email || "User";
  }

  /**
   * Check if user is of specific type
   * @param {number} userTypeId - User type ID to check
   * @returns {boolean} Whether user is of the specified type
   */
  isUserType(userTypeId) {
    const user = this.getCurrentUser();
    return user?.userTypeId === userTypeId;
  }

  /**
   * Check if user is customer (UserTypeID = 2)
   * @returns {boolean} Whether user is a customer
   */
  isCustomer() {
    return this.isUserType(2);
  }

  /**
   * Check if user is employee (UserTypeID = 1)
   * @returns {boolean} Whether user is an employee
   */
  isEmployee() {
    return this.isUserType(1) || this.isUserType(3);
  }

  /**
   * Check if user is brand user (UserTypeID = 3)
   * @returns {boolean} Whether user is a brand user
   */
  isBrandUser() {
    return this.isUserType(3);
  }

  /**
   * Check if user has access to multiple companies
   * @returns {boolean} Whether user has multiple company access
   */
  hasMultipleCompanies() {
    const user = this.getCurrentUser();
    return (user?.companies?.length || 0) > 1;
  }

  /**
   * Refresh user data from server
   * @returns {Promise<object|null>} Updated user data
   */
  async refreshUserData() {
    try {
      const user = this.getCurrentUser();
      if (!user) return null;

      // Refresh store data (customers only — employees have no stores)
      if (!this.isEmployee()) {
        await this.fetchUserStores();
      }

      return user;
    } catch (error) {
      console.error("Error refreshing user data:", error);
      return null;
    }
  }

  /**
   * KAN-149: Rehydrate the session from the HttpOnly auth cookie via the authenticated
   * `storefront/session` endpoint. Used on startup when there is no cached local session —
   * e.g. a fresh redirect from the marketing site after logging in there. On success, persists
   * the non-secret `user` object, session expiry, and primary company (mirroring login), then
   * returns the user. Returns null when there's no valid cookie (401) or on any error.
   * @private
   * @returns {Promise<object|null>}
   */
  async _hydrateSessionFromCookie() {
    try {
      // skipAuthRedirect: a 401 here just means "not logged in" — let the caller handle it
      // rather than triggering the global session-expired redirect.
      const { data: response } = await apiClient.get("/storefront/session", {
        skipAuthRedirect: true,
      });

      if (response?.status !== "Success" || !response.user) {
        return null;
      }

      const userData = {
        id: response.user.id,
        email: response.user.email,
        firstName: response.user.firstName,
        lastName: response.user.lastName,
        userTypeId: response.user.userTypeId,
        optStatusId: response.user.optStatusId,
        companyId: response.user.companyId,
        companies: response.user.companies || [],
        roleIds: response.user.roleIds || [],
        roles: response.user.roles || [],
        claims: response.user.claims || [],
        mustChangePassword: response.user.mustChangePassword || false,
      };

      tokenManager.setUser(userData);
      setAppInsightsUser(userData);
      if (response.expiration) {
        tokenManager.setSessionExpiry(response.expiration);
      }

      // Set primary company (mirrors login())
      if (userData.companies.length > 0) {
        const primaryCompany = userData.companies[0];
        tokenManager.setCurrentCompany({
          id: primaryCompany.companyId,
          name: primaryCompany.companyName,
          slug: primaryCompany.slug,
          displayName: primaryCompany.displayName,
          isMed: primaryCompany.isMed || false,
          isRec: primaryCompany.isRec || false,
          allowCustomCaseUnitLabels: primaryCompany.allowCustomCaseUnitLabels || false,
          moduleBackOrders: primaryCompany.moduleBackOrders === true,
        });
      }

      return userData;
    } catch (error) {
      // 401 / no cookie → not logged in. Any other error → treat as not authenticated.
      console.warn("Cookie session hydration failed:", error?.status ?? error);
      return null;
    }
  }

  /**
   * Initialize authentication state on app startup
   * @returns {Promise<boolean>} Whether authentication was restored
   */
  async initialize() {
    try {
      let user = tokenManager.getUser();

      if (!user) {
        // KAN-149 cookie SSO: no cached session, but the browser may carry a valid auth
        // cookie (e.g. redirected here from the marketing site after logging in there).
        // Hydrate from the cookie; if there's no valid cookie this returns null and we
        // fall through to "not authenticated".
        user = await this._hydrateSessionFromCookie();
        if (!user) {
          return false;
        }
      } else {
        // Cached session exists — confirm the cookie is still valid server-side.
        const validationResult = await this.validateToken();
        if (!validationResult.success) {
          return false;
        }

        // The cookie is shared with the admin portal, so it may now belong to someone else (e.g.
        // an employee who signed in there). Drop what's cached for the old user and become
        // whoever the cookie says, instead of showing one user while acting as another.
        if (this._cookieBelongsToSomeoneElse(validationResult.data)) {
          console.warn("The sign-in changed in another tab; switching to the signed-in user.");
          this.logout();
          user = await this._hydrateSessionFromCookie();
          if (!user) {
            return false;
          }
        }
      }

      // Restore user and company state
      this.currentUser = user;
      this.currentCompany = tokenManager.getCurrentCompany();

      if (this.isEmployee()) {
        // Employee — no stores to fetch, just restore company state
        this.availableStores = [];
        this.currentStore = null;
        return true;
      }

      // Customer — PRESERVE the selected store before fetching
      const savedStore = tokenManager.getCurrentStore();
      this.currentStore = savedStore;

      // Fetch latest store data. This clears the selection if the store has since been
      // inactivated or archived, which sends the user back to the picker.
      await this.fetchUserStores();

      // RESTORE the selected store after fetching, but use fresh data from availableStores.
      // A null currentStore here means the fetch succeeded and the store is gone, so leave it
      // cleared; a failed fetch leaves the selection alone and we keep the cached copy.
      if (savedStore && this.currentStore) {
        // Find the matching store from freshly fetched data to get updated company settings
        const freshStore = this.availableStores.find(s => s.id === savedStore.id);
        if (freshStore) {
          this.currentStore = freshStore;
          tokenManager.setCurrentStore(freshStore); // Update localStorage with fresh data
        }
      }

      return true;
    } catch (error) {
      console.error("Error initializing authentication:", error);
      this.logout();
      return false;
    }
  }
  /**
   * Handle authentication events
   */
  onAuthStateChange(callback) {
    // Simple event handling - you might want to use a more sophisticated event system
    this._authStateChangeCallback = callback;
  }

  /**
   * Notify auth state change
   * @private
   */
  _notifyAuthStateChange() {
    if (this._authStateChangeCallback) {
      this._authStateChangeCallback({
        isAuthenticated: this.isAuthenticated(),
        isFullyAuthenticated: this.isFullyAuthenticated(),
        user: this.getCurrentUser(),
        company: this.getCurrentCompany(),
        store: this.getCurrentStore(),
        availableStores: this.getAvailableStores(),
      });
    }
  }
}

// Create singleton instance
const authService = new AuthService();

// Export methods for easy use
export const {
  login,
  register,
  confirmProfile,
  logout,
  signOut,
  validateToken,
  getCurrentUser,
  getCurrentCompany,
  getCurrentStore,
  getAvailableStores,
  switchCompany,
  switchStore,
  selectStore,
  fetchUserStores,
  hasMultipleStores,
  hasStoreAccess,
  getStoreDisplayName,
  isAuthenticated,
  isFullyAuthenticated,
  hasRole,
  hasClaim,
  hasCompanyAccess,
  getUserCompanies,
  mustChangePassword,
  getUserDisplayName,
  isUserType,
  isCustomer,
  isEmployee,
  isBrandUser,
  hasMultipleCompanies,
  refreshUserData,
  initialize,
  onAuthStateChange,
} = authService;

// Export the service instance as default
export default authService;
